import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { UserRole } from './types/User';

import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import FloatingChat from './components/Chat/FloatingChat';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

import Home from './Pages/Home';
import Blog from './Pages/Blog';
import BlogPost from './components/Blog/BlogPost';
import FreeSolutions from './Pages/FreeSolutions';
import About from './Pages/About/About';
import Mensageria from './Pages/Mensageria/Mensageria';
import CustomSolutions from './Pages/CustomSolutions/CustomSolutions';
import Baby from './Pages/Baby/Baby';
import NFSe from './Pages/NFSe/NFSe';
import Termos from './Pages/Legal/Termos';
import Privacidade from './Pages/Legal/Privacidade';
import NotFound from './components/NotFound/NotFound';

import WhatsAppLinkGenerator from './free-tools/WhatsAppLinkGenerator/WhatsAppLinkGenerator';
import QRCodeGenerator from './free-tools/QRCodeGenerator/QRCodeGenerator';

import Login from './Pages/Auth/Login';
import Register from './Pages/Auth/Register';

import AdminDashboard from './Pages/Admin/AdminDashboard';
import useReveal from './hooks/useReveal';
import './styles/motion.css';
import './styles/professional.css';

const App: React.FC = () => {
  useReveal();

  return (
    <Router>
      <AuthProvider>
        <ScrollToTop />
        <Header />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/free-solutions" element={<FreeSolutions />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Free Tools */}
          <Route path="/free-tools/whatsapp-link-generator" element={<WhatsAppLinkGenerator />} />
          <Route path="/free-tools/qr-code-generator" element={<QRCodeGenerator />} />
          <Route path="/solucoes/mensageria" element={<Mensageria />} />
          <Route path="/custom-solutions" element={<CustomSolutions />} />
          <Route path="/solucoes/baby" element={<Baby />} />
          <Route path="/solucoes/nfse" element={<NFSe />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/privacidade" element={<Privacidade />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Admin Routes - Only Admin and Editor */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>Blog Manager</h1>
                  <p>Em desenvolvimento - Gerenciar posts do blog</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>User Manager</h1>
                  <p>Em desenvolvimento - Gerenciar usuários</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>Orders</h1>
                  <p>Em desenvolvimento - Ver todos os pedidos</p>
                </div>
              </ProtectedRoute>
            }
          />

          {/* Client Routes - Only Clients */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CLIENT]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>Client Dashboard</h1>
                  <p>Em desenvolvimento - Dashboard do cliente</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/products"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CLIENT]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>Products</h1>
                  <p>Em desenvolvimento - Ver produtos disponíveis</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/subscriptions"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CLIENT]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>My Subscriptions</h1>
                  <p>Em desenvolvimento - Minhas assinaturas</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/orders"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CLIENT]}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>My Orders</h1>
                  <p>Em desenvolvimento - Meus pedidos</p>
                </div>
              </ProtectedRoute>
            }
          />

          {/* Profile - All authenticated users */}
          <Route
            path="/dashboard/profile"
            element={
              <ProtectedRoute>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>My Profile</h1>
                  <p>Em desenvolvimento - Editar perfil</p>
                </div>
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="/notfound" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <FloatingChat />
        <Footer />
      </AuthProvider>
    </Router>
  );
};

export default App;
